import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ClientLink, ExportDropdown, Tag, Tags, Tooltip } from '~/components';
import ClientFiltersBar from '~/components/filters/ClientFiltersBar';
import { ContactFiltersBar, ContactFiltersGroup } from '~/components/filters';
import ClientFiltersGroup from '~/components/filters/ClientFiltersGroup';
import FilterButton from '~/components/filters/FilterButton';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import { useDocumentTitle, useSearchParams, useSearchParamsConfig } from '~/hooks';
import useClientFilters from '~/hooks/useClientFilters';
import useContactFilters from '~/hooks/useContactFilters';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort } from '~/utils';
import mimeTypes from '~/utils/mimeTypes';
import Icon from '../../../../components/Icon';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Report from '../components/Report';
import Table from '../components/table';
import ReportActions from '../ReportActions';

const Byline = styled.div`
  display: flex;
  padding-top: 0.5rem;
  font-size: 0.875rem;
`;
const ContactIcon = styled.div`
  display: flex;
  color: ${colors.primary};
  align-items: center;
  justify-content: center;
  width: 2rem;
`;
const ContactLabel = styled.div`
  text-align: left;
  color: ${colors.grey100};
`;
const Contact = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1rem;
`;

const Title = styled.p`
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  margin-left: 0.25rem;
`;

function FirstAssignment({ tags }) {
  const firstTag = tags && tags[0];
  if (!firstTag) return null;

  return (
    <Tag style={{ backgroundColor: colors.grey5 }}>
      <small>{firstTag.name}</small>
    </Tag>
  );
}

function Assignments({ tags }) {
  const count = tags.length - 1;
  if (count <= 0) return null;

  return (
    <Tooltip
      message={
        <div style={{ fontSize: '1rem' }}>
          <Title>Tags</Title>
          {tags.map((tag) => (
            <Tag style={{ backgroundColor: colors.grey5 }} key={tag.id}>
              <small>{tag.name}</small>
            </Tag>
          ))}
        </div>
      }>
      <Tag style={{ backgroundColor: colors.grey5, color: colors.grey40 }}>
        <small>+{count}</small>
      </Tag>
    </Tooltip>
  );
}

export default function ClientContacts({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const searchParamsConfig = useSearchParamsConfig();

  const clientFilters = useClientFilters({
    clientRecordStatusId: { ...searchParamsConfig.clientRecordStatusId, default: 'active' },
  });

  const contactFilters = useContactFilters();

  const [params, setParams] = useState({
    sort: new QuerySort('client.name', 'asc'),
    ...clientFilters.filters,
    ...contactFilters.filters,
  });

  // Init and sync URL search params
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        sort: { default: new QuerySort('client.name', 'asc'), ...searchParamsConfig.sort },
        ...clientFilters.searchParamsConfig,
        ...contactFilters.searchParamsConfig,
      }),
      [searchParamsConfig, clientFilters, contactFilters],
    ),

    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      sort: params.sort,
      ...clientFilters.mapUrlSearchParams(params),
      ...contactFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, contactFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, status: 'sorting' }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />

          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />
        <ContactFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const report = query.data;

  const groups = useMemo(() => {
    return _.groupBy(report.records, (record) => `${record.client.id}`);
  }, [report.records]);

  return (
    <Report.Table>
      <Table>
        <Table.Header>
          <Table.Column sticky minWidth="16rem" name="client.name" onSort={onSort} sort={params.sort}>
            Client
          </Table.Column>
          <Table.Column minWidth="16rem" name="contact.title">
            Title
          </Table.Column>
          <Table.Column width="22rem" name="contact" align="left">
            Contact Info
          </Table.Column>
          <Table.Column minWidth="10rem" name="contact.tags">
            Tags
          </Table.Column>
        </Table.Header>

        <Table.Body fade={query.status === 'sorting'}>
          {_.map(groups, (contacts, clientId) => {
            return (
              <React.Fragment key={clientId}>
                <Table.GroupRow top="2.95rem">
                  <Table.Cell>
                    <strong>
                      <ClientLink client={contacts[0].client} />
                    </strong>
                  </Table.Cell>
                </Table.GroupRow>

                {contacts.map((contact) => {
                  return (
                    <Table.Row key={contact.id}>
                      <Table.Cell>
                        <Contact>{contact.name}</Contact>
                      </Table.Cell>
                      <Table.Cell>{contact.title}</Table.Cell>
                      <Table.Cell>
                        <div>
                          {contact.email && (
                            <a href={`mailto:${contact.email}`}>
                              <Byline>
                                <ContactIcon>
                                  <Icon icon="at" />
                                </ContactIcon>
                                <ContactLabel>{contact.email}</ContactLabel>
                              </Byline>
                            </a>
                          )}
                          {contact.mobilePhone && (
                            <Byline>
                              <ContactIcon>
                                <Icon icon="mobile" />
                              </ContactIcon>
                              <ContactLabel>{contact.mobilePhone}</ContactLabel>
                            </Byline>
                          )}
                          {contact.officePhone && (
                            <Byline>
                              <ContactIcon>
                                <Icon icon="phone-office" />
                              </ContactIcon>
                              <ContactLabel>{contact.officePhone}</ContactLabel>
                            </Byline>
                          )}
                          {contact.fax > 0 && (
                            <Byline>
                              <ContactIcon>
                                <Icon icon="fax" />
                              </ContactIcon>
                              <ContactLabel>{contact.fax}</ContactLabel>
                            </Byline>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <Tags>
                          <FirstAssignment tags={contact.contact.tags} />
                          <Assignments tags={contact.contact.tags} />
                        </Tags>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </React.Fragment>
            );
          })}
        </Table.Body>

        <Table.Status
          total={_.size(groups)}
          label="Client"
          isLoading={query.status !== 'ready'}
          partial={report.partialResults}
        />
      </Table>
    </Report.Table>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <ClientFiltersGroup filters={filters} onChange={handleFilter} />
      <ContactFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}
